import { useQuery } from '@tanstack/react-query';
import { apiWrapper } from '@/apiWrapper';
import { getEndpoints } from '@/endpoints';
import type { AgentInfo } from '@/features/appointments/utils/AgentInfo';
import useThemeStore from '@/store/ThemeStore';

interface AgentsAndTimeslotsQueryBaseParams {
  workTypeId: string;
  inBranch: boolean;
}

interface AgentsAndTimeslotsQueryOnlineParams
  extends AgentsAndTimeslotsQueryBaseParams {
  inBranch: false;
  clubCode: string;
}

interface AgentsAndTimeslotsQueryInBranchParams
  extends AgentsAndTimeslotsQueryBaseParams {
  inBranch: true;
  branchId: string;
}

type AgentsAndTimeslotsQueryParams =
  | AgentsAndTimeslotsQueryOnlineParams
  | AgentsAndTimeslotsQueryInBranchParams;

interface UseAgentsAndTimeslotsQueryOptions {
  enabled?: boolean;
}

const getAgentInfoAndAppointments = async (
  params: AgentsAndTimeslotsQueryParams,
  isLuxury: boolean,
): Promise<AgentInfo[]> => {
  const url = getEndpoints().timeSlots.replace(
    '{workTypeId}',
    params.workTypeId,
  );
  const config = {
    method: 'GET',
    params: {
      clubCode: isLuxury || params.inBranch ? undefined : params.clubCode,
      serviceTerritoryId: params.inBranch ? params.branchId : undefined,
    },
  };

  return await apiWrapper<AgentInfo[]>(url, config);
};

export const useAgentsAndTimeslotsQuery = (
  params: AgentsAndTimeslotsQueryParams,
  options?: UseAgentsAndTimeslotsQueryOptions,
) => {
  const { enabled = true } = options || {};

  const isLuxuryTheme = useThemeStore((state) => state.isLuxuryTheme);

  return useQuery({
    queryKey: [
      'timeSlots',
      params.workTypeId,
      params.inBranch,
      params.inBranch ? params.branchId : params.clubCode,
      isLuxuryTheme,
    ],
    queryFn: () => getAgentInfoAndAppointments(params, isLuxuryTheme),
    enabled: enabled,
    staleTime: 2 * 60 * 1000,
  });
};
